import {Component, computed, ElementRef, inject, signal, viewChild} from '@angular/core';
import {DocumentStore} from '../services/document-store';
import {DocumentSummary, FolderSummary, FolderType} from '../../../../api';
import {SkeletonModule} from 'primeng/skeleton';
import {MessageService} from 'primeng/api';
import {ToastModule} from 'primeng/toast';
import {Loader} from '../../../components/loader/loader';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ElciaButton} from '../../../components/buttons/button/button';
import {ConfirmService} from '../../../services/confirm.service';
import {Divider} from 'primeng/divider';
import {ElciaDialog} from '../../../components/dialog/dialog';
import {FolderForm} from '../components/folder-form/folder-form';
import {ExpanderIcon} from '../../../components/expander-icon/expander-icon';
import {DocumentItem} from '../components/document-item/document-item';
import {ElciaIcon} from '../../../components/icon/icon';
import {PartyAvatar} from '../../../components/avatar/party-avatar.component';

interface FolderNode {
  folder: FolderSummary;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
}

@Component({
  selector: 'elcia-document-home',
  imports: [
    SkeletonModule,
    ToastModule,
    Loader,
    TranslatePipe,
    ElciaButton,
    Divider,
    ElciaDialog,
    FolderForm,
    ExpanderIcon,
    DocumentItem,
    ElciaIcon,
    PartyAvatar,
  ],
  providers: [MessageService, DocumentStore],
  templateUrl: './document-home.html',
  styleUrl: './document-home.scss',
})
export class DocumentHome {
  protected readonly store = inject(DocumentStore);
  private readonly confirmService = inject(ConfirmService);
  private readonly translateService = inject(TranslateService);

  protected showFolderDialog = signal(false);
  protected folderDialogInitial = signal<FolderSummary | undefined>(undefined);
  protected folderDialogParent = signal<FolderSummary | undefined>(undefined);
  protected readonly folderDialogParentId = computed(() => this.folderDialogParent()?.id ?? null);
  protected folderForm = viewChild(FolderForm);
  private fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected folderDialogTitle = computed(() =>
    this.folderDialogInitial() ? 'Modifier le dossier' : 'Nouveau dossier'
  );

  protected isFolderFormSaving = computed(() =>
    this.store.createFolderMutation.isPending() || this.store.moveFolderMutation.isPending()
  );

  protected draggedDocumentId = signal<number | null>(null);
  protected dragOverFolderId = signal<number | null>(null);
  protected dragPosition = signal<'before' | 'inside' | 'after'>('inside');
  protected dragOverRoot = signal(false);
  protected draggedFolderId = signal<number | null>(null);
  protected isDraggingFile = signal(false);
  private fileDragCounter = 0;
  protected expandedFolderIds = signal<Set<number>>(new Set());

  protected flatFolderNodes = computed(() =>
    this.buildFlatList(this.store.folders(), 0, this.expandedFolderIds())
  );

  private buildFlatList(
    folders: FolderSummary[],
    depth: number,
    expanded: Set<number>
  ): FolderNode[] {
    const result: FolderNode[] = [];
    const sorted = [...folders].sort((a, b) => a.orderNum - b.orderNum);
    for (const folder of sorted) {
      const hasChildren = folder.folders.length > 0;
      const isExpanded = expanded.has(folder.id);
      result.push({folder, depth, hasChildren, isExpanded});
      if (isExpanded && hasChildren) {
        result.push(...this.buildFlatList(folder.folders, depth + 1, expanded));
      }
    }
    return result;
  }

  protected toggleFolder(folderId: number, event: Event) {
    event.stopPropagation();
    const next = new Set(this.expandedFolderIds());
    if (next.has(folderId)) next.delete(folderId);
    else next.add(folderId);
    this.expandedFolderIds.set(next);
  }

  protected selectFolder(folderId: number) {
    this.store.selectedFolderId.set(folderId);
  }

  protected openCreateFolderDialog() {
    this.folderDialogInitial.set(undefined);
    this.folderDialogParent.set(undefined);
    this.showFolderDialog.set(true);
  }

  protected openCreateSubfolderDialog(folder: FolderSummary) {
    this.folderDialogInitial.set(undefined);
    this.folderDialogParent.set(folder);
    this.showFolderDialog.set(true);
  }

  protected openEditFolderDialog(folder: FolderSummary) {
    this.folderDialogInitial.set(folder);
    this.folderDialogParent.set(undefined);
    this.showFolderDialog.set(true);
  }

  protected closeFolderDialog() {
    this.showFolderDialog.set(false);
    this.folderDialogInitial.set(undefined);
    this.folderDialogParent.set(undefined);
  }

  protected onDialogSave() {
    this.folderForm()?.submit();
  }

  protected onFolderFormSubmit({name, userIds}: { name: string; userIds: number[] }) {
    const initial = this.folderDialogInitial();
    if (initial) {
      this.store.moveFolderMutation.mutate(
        {id: initial.id, parentId: initial.parentId ?? null, name, orderNum: initial.orderNum, userIds},
        {onSuccess: () => this.closeFolderDialog()}
      );
    } else {
      this.store.createFolderMutation.mutate(
        {name, parentId: this.folderDialogParentId(), userIds},
        {
          onSuccess: () => {
            const parentId = this.folderDialogParentId();
            if (parentId !== null) {
              const next = new Set(this.expandedFolderIds());
              next.add(parentId);
              this.expandedFolderIds.set(next);
            }
            this.closeFolderDialog();
          },
        }
      );
    }
  }

  protected deleteFolder(folder: FolderSummary) {
    this.confirmService.confirm({
      message: folder.name,
      title: this.translateService.instant('entity.folder'),
      accept: () => this.store.deleteFolderMutation.mutate(folder.id),
    });
  }

  protected openFilePicker() {
    this.fileInput()?.nativeElement.click();
  }

  protected onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    for (const file of files) {
      this.store.uploadDocumentMutation.mutate(
        {file, folderId: this.store.selectedFolderId()}
      );
    }
    input.value = '';
  }

  protected deleteDocument(doc: DocumentSummary) {
    this.confirmService.confirm({
      message: doc.name,
      title: this.translateService.instant('entity.document'),
      accept: () => this.store.deleteDocumentMutation.mutate(doc.id,),
    });
  }

  protected downloadDocument(doc: DocumentSummary) {
    this.store.downloadDocument(doc.id).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  // Drag & Drop - Folders
  protected onFolderDragStart(event: DragEvent, folder: FolderSummary) {
    event.dataTransfer?.setData('folder-id', String(folder.id));
    event.dataTransfer?.setData('drag-type', 'folder');
    this.draggedFolderId.set(folder.id);
  }

  protected onFolderDragOver(event: DragEvent, targetFolder: FolderSummary) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverRoot.set(false);
    this.dragOverFolderId.set(targetFolder.id);

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const y = event.clientY - rect.top;
    const ratio = y / rect.height;
    if (ratio < 0.25) {
      this.dragPosition.set('before');
    } else if (ratio > 0.75) {
      this.dragPosition.set('after');
    } else {
      this.dragPosition.set('inside');
    }
  }

  protected onFolderDragLeave() {
    this.dragOverFolderId.set(null);
    this.dragPosition.set('inside');
  }

  protected onFolderDragEnd() {
    this.draggedFolderId.set(null);
    this.dragOverFolderId.set(null);
    this.dragPosition.set('inside');
    this.dragOverRoot.set(false);
  }

  protected onFolderDrop(event: DragEvent, targetFolder: FolderSummary) {
    event.preventDefault();
    event.stopPropagation();
    const position = this.dragPosition();
    this.dragOverFolderId.set(null);
    this.dragPosition.set('inside');
    const dragType = event.dataTransfer?.getData('drag-type');

    if (dragType === 'folder') {
      const draggedId = Number(event.dataTransfer?.getData('folder-id'));
      if (!draggedId || draggedId === targetFolder.id) return;
      const dragged = this.store.allFolders().find(f => f.id === draggedId);
      if (!dragged) return;

      if (position === 'inside') {
        if (dragged.parentId === targetFolder.id) return;
        if (this.isDescendant(targetFolder.id, draggedId)) return;
        this.store.moveFolderMutation.mutate({
          id: draggedId,
          parentId: targetFolder.id,
          name: dragged.name,
          orderNum: dragged.orderNum,
          userIds: dragged.userAccess.map(u => u.id),
        });
      } else {
        const newParentId = targetFolder.parentId ?? null;
        if (newParentId !== null && this.isDescendant(newParentId, draggedId)) return;
        const siblings = this.getSiblings(newParentId);
        const orderNum = this.calculateOrderNum(siblings, targetFolder, position, draggedId);
        if (dragged.parentId === newParentId && dragged.orderNum === orderNum) return;
        this.store.moveFolderMutation.mutate({
          id: draggedId,
          parentId: newParentId,
          name: dragged.name,
          orderNum,
          userIds: dragged.userAccess.map(u => u.id),
        });
      }
    } else if (dragType === 'document') {
      const docId = Number(event.dataTransfer?.getData('document-id'));
      if (!docId) return;
      this.store.moveDocumentFolderMutation.mutate({documentId: docId, folderId: targetFolder.id});
    }
  }

  // Drag & Drop - Root zone
  protected onRootDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverRoot.set(true);
    this.dragOverFolderId.set(null);
  }

  protected onRootDragLeave() {
    this.dragOverRoot.set(false);
  }

  protected onRootDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverRoot.set(false);
    const dragType = event.dataTransfer?.getData('drag-type');
    if (dragType !== 'folder') return;

    const draggedId = Number(event.dataTransfer?.getData('folder-id'));
    if (!draggedId) return;
    const dragged = this.store.allFolders().find(f => f.id === draggedId);
    if (!dragged || dragged.parentId == null) return;

    const rootSiblings = this.getSiblings(null);
    const maxOrder = rootSiblings.reduce((max, f) => Math.max(max, f.orderNum), 0);
    this.store.moveFolderMutation.mutate({
      id: draggedId,
      parentId: null,
      name: dragged.name,
      orderNum: maxOrder + 1,
      userIds: dragged.userAccess.map(u => u.id),
    });
  }

  private getSiblings(parentId: number | null): FolderSummary[] {
    if (parentId === null) return this.store.folders();
    const parent = this.store.allFolders().find(f => f.id === parentId);
    return parent?.folders ?? [];
  }

  private calculateOrderNum(
    siblings: FolderSummary[],
    target: FolderSummary,
    position: 'before' | 'after',
    draggedId: number
  ): number {
    const sorted = [...siblings]
      .filter(f => f.id !== draggedId)
      .sort((a, b) => a.orderNum - b.orderNum);
    const targetIndex = sorted.findIndex(f => f.id === target.id);
    if (targetIndex === -1) return target.orderNum;

    if (position === 'before') {
      const prev = sorted[targetIndex - 1];
      return prev ? (prev.orderNum + target.orderNum) / 2 : target.orderNum - 1;
    } else {
      const next = sorted[targetIndex + 1];
      return next ? (target.orderNum + next.orderNum) / 2 : target.orderNum + 1;
    }
  }

  // Drag & Drop - Documents
  protected onDocumentDragStart(event: DragEvent, doc: DocumentSummary) {
    event.dataTransfer?.setData('document-id', String(doc.id));
    event.dataTransfer?.setData('drag-type', 'document');
    this.draggedDocumentId.set(doc.id);
  }

  protected onDocumentDragEnd() {
    this.draggedDocumentId.set(null);
  }

  // Drag & Drop - File from OS
  protected onFileDragEnter(event: DragEvent) {
    if (!event.dataTransfer?.types.includes('Files')) return;
    this.fileDragCounter++;
    this.isDraggingFile.set(true);
  }

  protected onFileDragOver(event: DragEvent) {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
  }

  protected onFileDragLeave() {
    this.fileDragCounter--;
    if (this.fileDragCounter === 0) {
      this.isDraggingFile.set(false);
    }
  }

  protected onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.fileDragCounter = 0;
    this.isDraggingFile.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    for (const file of files) {
      this.store.uploadDocumentMutation.mutate(
        {file, folderId: this.store.selectedFolderId()}
      );
    }
  }

  private isDescendant(targetId: number, ancestorId: number): boolean {
    const all = this.store.allFolders();
    let current = all.find(f => f.id === targetId);
    while (current) {
      if (current.parentId === ancestorId) return true;
      current = all.find(f => f.id === current!.parentId);
    }
    return false;
  }

  protected readonly FolderType = FolderType;
}
